import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, WalletIcon } from '@heroicons/react/24/outline'
import { supabase } from '@/lib/supabase'

interface Wallet {
  id: string
  wallet_name: string
  wallet_address: string
  channel_code: string
  channel_name: string
  channel_icon: string
  is_default: boolean
  is_enabled: boolean
  account_holder_name?: string
  bank_name?: string
  bank_branch?: string
  network?: string
  created_at: string
}

interface OperatorDetail {
  user: {
    id: string
    email: string
    full_name: string
    role: 'admin' | 'finance' | 'audit' | 'operator'
    is_active: boolean
    permissions: string[]
    last_login_at?: string
    created_at: string
    updated_at: string
    // 运营商特有字段
    operator_id?: string
    commission_rate?: number
  }
  account?: {
    balance: number
    total_commission: number
    total_withdraw: number
    pending_commission: number
  }
  user_stats?: {
    total_users: number
    active_users: number
    inactive_users: number
    total_user_volume: number
    total_user_profit: number
    total_user_loss: number
    recent_users: number
    recent_commission: number
  }
  recent_activities?: Array<{
    id: string
    type: string
    amount: number
    description: string
    created_at: string
    user_id?: string
  }>
}

export default function OperatorDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [operator, setOperator] = useState<OperatorDetail | null>(null)
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    commission_rate: 0,
  })
  
  // 批量创建用户相关状态
  const [showBatchCreateModal, setShowBatchCreateModal] = useState(false)
  const [batchCreateCount, setBatchCreateCount] = useState(10)
  const [batchCreateBalance, setBatchCreateBalance] = useState(0)
  const [isBatchCreating, setIsBatchCreating] = useState(false)
  const [batchCreateResult, setBatchCreateResult] = useState<any>(null)

  useEffect(() => {
    fetchOperatorDetail()
    fetchOperatorWallets()
  }, [id])

  const fetchOperatorDetail = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getOperatorDetail(id!)
      if (response.success && response.data) {
        setOperator(response.data)
        setEditData({
          name: response.data.user.full_name,
          email: response.data.user.email,
          commission_rate: response.data.user.commission_rate || 0,
        })
      } else {
        setError(response.message || '加载运营商详情失败')
      }
    } catch (err) {
      setError('加载运营商详情失败')
      console.error('Failed to fetch operator detail:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchOperatorWallets = async () => {
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select(`
          id,
          wallet_name,
          wallet_address,
          channel_code,
          is_default,
          is_enabled,
          account_holder_name,
          bank_name,
          bank_branch,
          network,
          created_at,
          payment_channels(channel_name, channel_icon)
        `)
        .eq('owner_id', id)
        .eq('owner_type', 'operator')
        .order('created_at', { ascending: false })

      if (error) throw error

      if (data) {
        const walletsWithChannels = data.map((wallet: any) => ({
          id: wallet.id,
          wallet_name: wallet.wallet_name,
          wallet_address: wallet.wallet_address,
          channel_code: wallet.channel_code,
          channel_name: wallet.payment_channels?.channel_name || '',
          channel_icon: wallet.payment_channels?.channel_icon || '',
          is_default: wallet.is_default,
          is_enabled: wallet.is_enabled,
          account_holder_name: wallet.account_holder_name,
          bank_name: wallet.bank_name,
          bank_branch: wallet.bank_branch,
          network: wallet.network,
          created_at: wallet.created_at,
        }))
        setWallets(walletsWithChannels)
      }
    } catch (err) {
      console.error('Failed to fetch operator wallets:', err)
    }
  }

  const handleSaveChanges = async () => {
    try {
      const response = await apiClient.updateOperator({
        operator_id: id,
        ...editData,
      })
      if (response.success) {
        setIsEditing(false)
        fetchOperatorDetail()
      } else {
        setError(response.message || '更新失败')
      }
    } catch (err) {
      setError('更新失败')
      console.error('Failed to update operator:', err)
    }
  }

  const handleBatchCreateUsers = async () => {
    if (!id) return
    
    try {
      setIsBatchCreating(true)
      setError(null)
      
      const response = await apiClient.batchCreateUsers(
        id,
        batchCreateCount,
        batchCreateBalance
      )
      
      if (response.success) {
        setBatchCreateResult(response.data)
        // 刷新运营商详情以更新用户统计
        fetchOperatorDetail()
      } else {
        setError(response.message || '批量创建失败')
      }
    } catch (err) {
      setError('批量创建失败')
      console.error('Failed to batch create users:', err)
    } finally {
      setIsBatchCreating(false)
    }
  }

  const handleCloseBatchCreateModal = () => {
    setShowBatchCreateModal(false)
    setBatchCreateResult(null)
    setBatchCreateCount(10)
    setBatchCreateBalance(0)
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: '超级管理员',
      finance: '财务管理员',
      audit: '审计管理员',
      operator: '运营商'
    }
    return labels[role] || role
  }

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800',
      finance: 'bg-blue-100 text-blue-800',
      audit: 'bg-yellow-100 text-yellow-800',
      operator: 'bg-green-100 text-green-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const formatPercent = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
        <CheckCircleIcon className="w-4 h-4" />
        活跃
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium">
        <XCircleIcon className="w-4 h-4" />
        禁用
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="loading-spinner w-8 h-8"></div>
            <p className="text-sm text-gray-500">加载中...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !operator) {
    return (
      <div className="page-content">
        <div className="mb-8">
          <button
            onClick={() => navigate('/operators')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            返回运营商列表
          </button>
          <h1 className="page-title">运营商详情</h1>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="text-center py-8">
              <p className="text-red-600">{error || '运营商不存在'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/operators')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-3"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          返回后台用户列表
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{operator.user.full_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(operator.user.role)}`}>
                {getRoleLabel(operator.user.role)}
              </span>
              <p className="text-sm text-gray-600">{operator.user.email}</p>
            </div>
          </div>
          <div>{getStatusBadge(operator.user.is_active)}</div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="card">
          <div className="card-header flex justify-between items-center py-3">
            <h2 className="text-base font-semibold">基本信息</h2>
            {!isEditing && operator.user.role === 'operator' && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                编辑
              </button>
            )}
          </div>
          <div className="card-body py-3 space-y-3">
            {isEditing ? (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-600">用户名称</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">邮箱</label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                {operator.user.role === 'operator' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-600">佣金比例 (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={editData.commission_rate * 100}
                        onChange={(e) => setEditData({ ...editData, commission_rate: parseFloat(e.target.value) / 100 })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveChanges}
                    className="flex-1 btn btn-primary"
                  >
                    保存
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="flex-1 btn btn-secondary"
                  >
                    取消
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-600">角色</label>
                  <p className="text-gray-900 mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(operator.user.role)}`}>
                      {getRoleLabel(operator.user.role)}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">邮箱</label>
                  <p className="text-gray-900 mt-1">{operator.user.email}</p>
                </div>
                {operator.user.role === 'operator' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-600">佣金比例</label>
                      <p className="text-gray-900 mt-1">{formatPercent(operator.user.commission_rate || 0)}</p>
                    </div>
                  </>
                )}
                {operator.user.role !== 'operator' && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">权限</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {operator.user.permissions.map((perm, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                          {perm}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Account Info - 只对运营商显示 */}
        {operator.user.role === 'operator' && operator.account && (
          <div className="card">
            <div className="card-header py-3">
              <h2 className="text-base font-semibold">账户信息</h2>
            </div>
            <div className="card-body py-3 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">佣金余额</label>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {formatCurrency(operator.account.balance)}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">待结佣金</label>
                <p className="text-base font-semibold text-orange-600 mt-0.5">
                  {formatCurrency(operator.account.pending_commission)}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">累计佣金</label>
                <p className="text-base font-semibold text-green-600 mt-0.5">
                  {formatCurrency(operator.account.total_commission)}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">累计提现</label>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {formatCurrency(operator.account.total_withdraw)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 管理员信息 */}
        {operator.user.role !== 'operator' && (
          <div className="card">
            <div className="card-header py-3">
              <h2 className="text-base font-semibold">管理员信息</h2>
            </div>
            <div className="card-body py-3 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600">角色类型</label>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {getRoleLabel(operator.user.role)}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">权限数量</label>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {operator.user.permissions.length} 个权限
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">账户状态</label>
                <p className="text-base font-semibold text-gray-900 mt-0.5">
                  {operator.user.is_active ? '✅ 活跃' : '❌ 禁用'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Time Info */}
        <div className="card">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">时间信息</h2>
          </div>
          <div className="card-body py-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-600">创建时间</label>
              <p className="text-sm text-gray-900 mt-0.5">{formatDate(operator.user.created_at)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">最后更新</label>
              <p className="text-sm text-gray-900 mt-0.5">{formatDate(operator.user.updated_at)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">最后登录</label>
              <p className="text-sm text-gray-900 mt-0.5">
                {operator.user.last_login_at ? formatDate(operator.user.last_login_at) : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Wallets Info - 只对运营商显示 */}
      {operator.user.role === 'operator' && (
        <div className="card mb-4">
          <div className="card-header py-3">
            <h2 className="text-base font-semibold">钱包信息</h2>
          </div>
          <div className="card-body py-3">
            {wallets.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {wallets.map((wallet) => (
                  <div key={wallet.id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{wallet.channel_icon}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{wallet.wallet_name}</div>
                          <div className="text-xs text-gray-500">{wallet.channel_name}</div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        {wallet.is_enabled ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            启用
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            禁用
                          </span>
                        )}
                        {wallet.is_default && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            默认
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {wallet.channel_code === 'bank_transfer' && (
                        <>
                          <div className="text-xs text-gray-600">
                            <span className="text-gray-500">账户名:</span> {wallet.account_holder_name}
                          </div>
                          <div className="text-xs text-gray-600">
                            <span className="text-gray-500">银行:</span> {wallet.bank_name}
                          </div>
                          <div className="text-xs text-gray-600 font-mono break-all">
                            <span className="text-gray-500">卡号:</span> {wallet.wallet_address}
                          </div>
                        </>
                      )}
                      {wallet.channel_code === 'okx' && (
                        <>
                          <div className="text-xs text-gray-600 font-mono break-all">
                            <span className="text-gray-500">地址:</span> {wallet.wallet_address}
                          </div>
                          {wallet.network && (
                            <div className="text-xs text-orange-600">
                              <span className="text-gray-500">网络:</span> {wallet.network}
                            </div>
                          )}
                        </>
                      )}
                      {(wallet.channel_code === 'alipay' || wallet.channel_code === 'wechat') && (
                        <div className="text-xs text-gray-600 break-all">
                          <span className="text-gray-500">账户:</span> {wallet.wallet_address}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(wallet.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <WalletIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm">暂无钱包</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Stats - 只对运营商显示 */}
      {operator.user.role === 'operator' && operator.user_stats && (
        <>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-semibold text-gray-900">用户统计</h2>
            <button
              onClick={() => setShowBatchCreateModal(true)}
              className="btn btn-primary text-sm py-1.5 px-3"
            >
              批量创建用户
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="card">
              <div className="card-body py-3">
                <p className="text-xs text-gray-600">总用户数</p>
                <p className="text-xl font-bold text-gray-900 mt-1">
                  {operator.user_stats.total_users}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  活跃: {operator.user_stats.active_users}
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-body py-3">
                <p className="text-xs text-gray-600">本月新增</p>
                <p className="text-xl font-bold text-gray-900 mt-1">
                  {operator.user_stats.recent_users}
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-body py-3">
                <p className="text-xs text-gray-600">交易总量</p>
                <p className="text-xl font-bold text-gray-900 mt-1">
                  {formatCurrency(operator.user_stats.total_user_volume || 0)}
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-body py-3">
                <p className="text-xs text-gray-600">本月佣金</p>
                <p className="text-xl font-bold text-green-600 mt-1">
                  {formatCurrency(operator.user_stats.recent_commission || 0)}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Trading Stats and Commission Info - 只对运营商显示 */}
      {operator.user.role === 'operator' && operator.user_stats && operator.account && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <div className="card">
            <div className="card-header py-3">
              <h2 className="text-base font-semibold">用户交易统计</h2>
            </div>
            <div className="card-body py-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">用户总数</span>
                <span className="text-lg font-bold text-gray-900">{operator.user_stats.total_users}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">活跃用户</span>
                <span className="text-lg font-bold text-green-600">{operator.user_stats.active_users}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">非活跃用户</span>
                <span className="text-lg font-bold text-gray-900">{operator.user_stats.inactive_users || 0}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-sm text-gray-600">用户总盈利</span>
                <span className="text-lg font-bold text-green-600">{formatCurrency(operator.user_stats.total_user_profit || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">用户总亏损</span>
                <span className="text-lg font-bold text-red-600">{formatCurrency(operator.user_stats.total_user_loss || 0)}</span>
              </div>
            </div>
          </div>

          {/* Commission Info */}
          <div className="card">
            <div className="card-header py-3">
              <h2 className="text-base font-semibold">佣金信息</h2>
            </div>
            <div className="card-body py-3 space-y-2">
              <div>
                <label className="text-xs font-medium text-gray-600">佣金比例</label>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{formatPercent(operator.user.commission_rate || 0)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">累计佣金</label>
                <p className="text-lg font-bold text-green-600 mt-0.5">{formatCurrency(operator.account.total_commission)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">待结佣金</label>
                <p className="text-lg font-bold text-orange-600 mt-0.5">{formatCurrency(operator.account.pending_commission)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">已提现佣金</label>
                <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(operator.account.total_withdraw)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Info */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">时间信息</h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-600">创建时间</label>
            <p className="text-gray-900 mt-1">{formatDate(operator.user.created_at)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">最后更新</label>
            <p className="text-gray-900 mt-1">{formatDate(operator.user.updated_at)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">最后登录</label>
            <p className="text-gray-900 mt-1">
              {operator.user.last_login_at ? formatDate(operator.user.last_login_at) : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activities - 只对运营商显示 */}
      {operator.user.role === 'operator' && operator.recent_activities && operator.recent_activities.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">最近活动</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">类型</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金额</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">时间</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {operator.recent_activities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{activity.type}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {formatCurrency(activity.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{activity.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(activity.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* 批量创建用户模态框 */}
      {showBatchCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">批量创建用户</h2>
                <button
                  onClick={handleCloseBatchCreateModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              {!batchCreateResult ? (
                <>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        创建数量
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={batchCreateCount}
                        onChange={(e) => setBatchCreateCount(parseInt(e.target.value) || 10)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="输入要创建的用户数量 (1-100)"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        将为运营商 {operator?.user.full_name} 批量创建 {batchCreateCount} 个测试用户
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        初始余额 (可选)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={batchCreateBalance}
                        onChange={(e) => setBatchCreateBalance(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="输入初始余额"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        每个用户将获得 {formatCurrency(batchCreateBalance)} 的初始余额
                      </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                      <p className="text-sm text-yellow-800">
                        <strong>注意：</strong>
                        <br />
                        • 系统将自动生成随机的邮箱和密码
                        <br />
                        • 创建完成后会显示所有用户的登录信息
                        <br />
                        • 请妥善保存用户的登录凭据
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleBatchCreateUsers}
                      disabled={isBatchCreating || batchCreateCount < 1 || batchCreateCount > 100}
                      className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isBatchCreating ? (
                        <>
                          <div className="loading-spinner w-4 h-4 mr-2"></div>
                          创建中...
                        </>
                      ) : (
                        `创建 ${batchCreateCount} 个用户`
                      )}
                    </button>
                    <button
                      onClick={handleCloseBatchCreateModal}
                      disabled={isBatchCreating}
                      className="flex-1 btn btn-secondary"
                    >
                      取消
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                      <p className="text-sm text-green-800">
                        <CheckCircleIcon className="w-5 h-5 inline mr-2" />
                        批量创建完成！成功创建 {batchCreateResult.summary.success} 个用户
                        {batchCreateResult.summary.failed > 0 && `, 失败 ${batchCreateResult.summary.failed} 个`}
                      </p>
                    </div>

                    {batchCreateResult.created_users && batchCreateResult.created_users.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">创建成功的用户：</h3>
                        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">邮箱</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">姓名</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">密码</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {batchCreateResult.created_users.map((user: any, index: number) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-gray-900 font-mono text-xs">{user.email}</td>
                                  <td className="px-4 py-2 text-gray-900">{user.full_name}</td>
                                  <td className="px-4 py-2 text-gray-900 font-mono text-xs">{user.password}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="mt-2 text-xs text-red-600">
                          ⚠️ 请立即复制保存这些登录信息，关闭后将无法再次查看密码！
                        </p>
                      </div>
                    )}

                    {batchCreateResult.errors && batchCreateResult.errors.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-red-900 mb-2">创建失败的用户：</h3>
                        <div className="max-h-48 overflow-y-auto border border-red-200 rounded-md bg-red-50 p-3">
                          {batchCreateResult.errors.map((error: any, index: number) => (
                            <div key={index} className="text-xs text-red-800 mb-1">
                              #{error.index}: {error.email || '未知'} - {error.error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        // 复制所有用户信息到剪贴板
                        const text = batchCreateResult.created_users
                          .map((u: any) => `${u.email}\t${u.full_name}\t${u.password}`)
                          .join('\n')
                        navigator.clipboard.writeText(text)
                        alert('用户信息已复制到剪贴板')
                      }}
                      className="flex-1 btn btn-primary"
                    >
                      复制所有用户信息
                    </button>
                    <button
                      onClick={handleCloseBatchCreateModal}
                      className="flex-1 btn btn-secondary"
                    >
                      关闭
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}